import { useState } from "react";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/style.css";
import { useModal } from "../../contexts/ModalContext";
import { useLanguage } from "../../contexts/LanguageContext";
import { motion } from "framer-motion";

export default function DevGeniusSignup() {
  const [password, setPassword] = useState("");
  const [passwordStrength, setPasswordStrength] = useState({ text: "", className: "", level: 0 });
  const [yearsInMarket, setYearsInMarket] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [phone, setPhone] = useState("");
  const { showModal } = useModal();
  const { t } = useLanguage();

  const checkPasswordStrength = (inputPassword) => {
    let strength = 0;
    if (inputPassword.length >= 8) strength++;
    if (inputPassword.match(/[a-z]/) && inputPassword.match(/[A-Z]/)) strength++;
    if (inputPassword.match(/\d/)) strength++;
    if (inputPassword.match(/[^a-zA-Z\d]/)) strength++;

    switch (strength) {
      case 0:
      case 1:
        return { text: t("weak"), color: "#f87171", level: strength };
      case 2:
        return { text: t("fair"), color: "#f59e0b", level: strength };
      default:
        return { text: t("strong"), color: "#10b981", level: strength };
    }
  };

  const handlePasswordChange = (event) => {
    const nextPassword = event.target.value;
    setPassword(nextPassword);
    setPasswordStrength(checkPasswordStrength(nextPassword));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setIsSubmitting(true);
    setTimeout(() => {
      setIsSubmitting(false);
      showModal(t("signupReceived"), t("signupModalTitle"));
      event.target.reset();
      setPhone("");
      setPassword("");
      setYearsInMarket("");
      setPasswordStrength({ text: "", color: "", level: 0 });
    }, 400);
  };

  const inputStyle = {
    width: '100%',
    padding: '14px 18px',
    background: 'var(--input-bg)',
    border: '1px solid var(--input-border)',
    borderRadius: '14px',
    color: 'var(--text-primary)',
    fontSize: '0.9rem',
    fontFamily: 'var(--font-body)',
    outline: 'none',
    transition: 'border-color 0.3s ease, box-shadow 0.3s ease, background 0.3s ease',
  };

  const labelStyle = {
    display: 'block',
    marginBottom: '8px',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: 'var(--text-secondary)',
    letterSpacing: '0.02em',
  };

  const fieldAnim = (i) => ({
    initial: { opacity: 0, y: 15 },
    animate: { opacity: 1, y: 0 },
    transition: { duration: 0.4, delay: 0.1 + i * 0.06 },
  });

  return (
    <div style={{
      minHeight: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '40px 20px',
      position: 'relative',
    }}>
      {/* Floating decorations */}
      <div style={{
        position: 'absolute',
        top: '10%',
        left: '8%',
        width: '200px',
        height: '200px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(245, 158, 11, 0.06), transparent 70%)',
        animation: 'float 8s ease-in-out infinite',
        pointerEvents: 'none',
      }} />
      <div style={{
        position: 'absolute',
        bottom: '15%',
        right: '10%',
        width: '160px',
        height: '160px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(124, 58, 237, 0.06), transparent 70%)',
        animation: 'floatSlow 10s ease-in-out infinite',
        pointerEvents: 'none',
      }} />

      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
        style={{
          width: '100%',
          maxWidth: '560px',
          background: 'var(--bg-card)',
          backdropFilter: 'var(--card-backdrop)',
          WebkitBackdropFilter: 'var(--card-backdrop)',
          border: '1px solid var(--border-glass)',
          borderRadius: '28px',
          padding: '48px 40px',
          boxShadow: 'var(--shadow-lg)',
          position: 'relative',
          overflow: 'hidden',
        }}
      >
        {/* Top gradient accent */}
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '3px',
          background: 'linear-gradient(90deg, var(--accent-cyan), #f59e0b, var(--accent-violet))',
        }} />

        <motion.div {...fieldAnim(0)}>
          <div style={{
            display: 'inline-block',
            padding: '4px 14px',
            borderRadius: '9999px',
            background: 'rgba(245, 158, 11, 0.1)',
            border: '1px solid rgba(245, 158, 11, 0.2)',
            fontSize: '0.7rem',
            fontWeight: 700,
            color: '#f59e0b',
            textTransform: 'uppercase',
            letterSpacing: '0.1em',
            marginBottom: '20px',
          }}>
            {t("businessAccount")}
          </div>
          <h1 style={{
            fontFamily: 'var(--font-display)',
            fontSize: '1.8rem',
            fontWeight: 700,
            background: 'linear-gradient(135deg, #f59e0b, var(--accent-violet))',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            marginBottom: '32px',
          }}>
            {t("businessSignupTitle")}
          </h1>
        </motion.div>

        <form id="clientSignupForm" onSubmit={handleSubmit} style={{ display: 'grid', gap: '20px' }}>
          <motion.div {...fieldAnim(1)}>
            <label htmlFor="fullName" style={labelStyle}>{t("fullNameBusinessLabel")}</label>
            <input
              type="text"
              style={inputStyle}
              id="fullName"
              placeholder={t("fullNameBusinessPlaceholder")}
              required
              onFocus={e => { e.target.style.borderColor = 'rgba(245, 158, 11, 0.4)'; e.target.style.boxShadow = '0 0 20px rgba(245, 158, 11, 0.06)'; }}
              onBlur={e => { e.target.style.borderColor = 'var(--border-glass)'; e.target.style.boxShadow = 'none'; }}
            />
          </motion.div>

          <motion.div {...fieldAnim(2)}>
            <label htmlFor="email" style={labelStyle}>{t("emailAddressRequired")}</label>
            <input
              type="email"
              style={inputStyle}
              id="email"
              placeholder={t("emailAddress")}
              required
              onFocus={e => { e.target.style.borderColor = 'rgba(245, 158, 11, 0.4)'; e.target.style.boxShadow = '0 0 20px rgba(245, 158, 11, 0.06)'; }}
              onBlur={e => { e.target.style.borderColor = 'var(--border-glass)'; e.target.style.boxShadow = 'none'; }}
            />
          </motion.div>

          <motion.div {...fieldAnim(3)}>
            <label htmlFor="password" style={labelStyle}>{t("password")} *</label>
            <input
              type="password"
              style={inputStyle}
              id="password"
              placeholder={t("createSecurePassword")}
              required
              value={password}
              onChange={handlePasswordChange}
              onFocus={e => { e.target.style.borderColor = 'rgba(245, 158, 11, 0.4)'; e.target.style.boxShadow = '0 0 20px rgba(245, 158, 11, 0.06)'; }}
              onBlur={e => { e.target.style.borderColor = 'var(--border-glass)'; e.target.style.boxShadow = 'none'; }}
            />
            {password && (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px' }}>
                <div style={{
                  flex: 1,
                  height: '3px',
                  borderRadius: '2px',
                  background: 'var(--border-subtle)',
                  overflow: 'hidden',
                }}>
                  <div style={{
                    width: `${(passwordStrength.level / 4) * 100}%`,
                    height: '100%',
                    borderRadius: '2px',
                    background: passwordStrength.color,
                    transition: 'all 0.3s ease',
                  }} />
                </div>
                <span style={{
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  color: passwordStrength.color,
                }}>
                  {passwordStrength.text}
                </span>
              </div>
            )}
          </motion.div>

          <motion.div {...fieldAnim(4)}>
            <label htmlFor="phone" style={labelStyle}>{t("phoneNumberLabel")}</label>
            <PhoneInput
              country={"us"}
              value={phone}
              onChange={(value) => setPhone(value)}
              enableAreaCodes
              enableLongNumbers
              inputProps={{
                name: "phone",
                required: true,
              }}
              containerStyle={{ display: "flex", alignItems: "center" }}
              inputStyle={{
                width: "100%",
                paddingLeft: "50px",
                background: 'var(--input-bg)',
                border: '1px solid var(--input-border)',
                borderRadius: '14px',
                color: 'var(--text-primary)',
                fontSize: '0.9rem',
                height: '48px',
              }}
              buttonStyle={{
                position: "absolute",
                left: "10px",
                zIndex: 1,
                background: 'transparent',
                border: 'none',
              }}
            />
          </motion.div>

          {/* Section divider */}
          <motion.div {...fieldAnim(5)} style={{
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
            margin: '8px 0',
          }}>
            <div style={{ flex: 1, height: '1px', background: 'var(--border-subtle)' }} />
            <span style={{
              fontSize: '0.8rem',
              fontWeight: 600,
              color: 'var(--text-muted)',
              textTransform: 'uppercase',
              letterSpacing: '0.1em',
            }}>{t("companyDetails")}</span>
            <div style={{ flex: 1, height: '1px', background: 'var(--border-subtle)' }} />
          </motion.div>

          <motion.div {...fieldAnim(6)}>
            <label htmlFor="companyDescription" style={labelStyle}>{t("companyDescriptionLabel")}</label>
            <textarea
              style={{ ...inputStyle, resize: 'vertical', minHeight: '100px' }}
              id="companyDescription"
              rows="3"
              placeholder={t("companyDescriptionPlaceholder")}
              required
              onFocus={e => { e.target.style.borderColor = 'rgba(245, 158, 11, 0.4)'; e.target.style.boxShadow = '0 0 20px rgba(245, 158, 11, 0.06)'; }}
              onBlur={e => { e.target.style.borderColor = 'var(--border-glass)'; e.target.style.boxShadow = 'none'; }}
            />
          </motion.div>

          <motion.div {...fieldAnim(7)}>
            <label htmlFor="yearsInMarket" style={labelStyle}>{t("yearsInMarketLabel")}</label>
            <select
              style={{ ...inputStyle, cursor: 'pointer' }}
              id="yearsInMarket"
              required
              value={yearsInMarket}
              onChange={(event) => setYearsInMarket(event.target.value)}
              onFocus={e => { e.target.style.borderColor = 'rgba(245, 158, 11, 0.4)'; }}
              onBlur={e => { e.target.style.borderColor = 'var(--border-glass)'; }}
            >
              <option value="" disabled>{t("yearsInMarketPlaceholder")}</option>
              <option value="1">{t("lessThan1Year")}</option>
              <option value="1-3">{t("years1to3")}</option>
              <option value="3-5">{t("years3to5")}</option>
              <option value="5+">{t("moreThan5Years")}</option>
            </select>
          </motion.div>

          <motion.div {...fieldAnim(8)}>
            <p style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: '16px' }}>
              {t("mandatoryFieldsNote")}
            </p>
            <button
              type="submit"
              disabled={isSubmitting}
              style={{
                width: '100%',
                padding: '16px',
                borderRadius: '14px',
                border: 'none',
                background: isSubmitting
                  ? 'var(--bg-glass)'
                  : 'linear-gradient(135deg, #f59e0b, var(--accent-violet))',
                color: isSubmitting ? 'var(--text-muted)' : '#fff',
                fontSize: '1rem',
                fontWeight: 700,
                fontFamily: 'var(--font-display)',
                cursor: isSubmitting ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: isSubmitting ? 'none' : '0 8px 24px rgba(245, 158, 11, 0.2)',
              }}
              onMouseEnter={e => {
                if (!isSubmitting) {
                  e.currentTarget.style.boxShadow = '0 12px 32px rgba(245, 158, 11, 0.35)';
                  e.currentTarget.style.transform = 'translateY(-2px)';
                }
              }}
              onMouseLeave={e => {
                if (!isSubmitting) {
                  e.currentTarget.style.boxShadow = '0 8px 24px rgba(245, 158, 11, 0.2)';
                  e.currentTarget.style.transform = 'translateY(0)';
                }
              }}
            >
              {isSubmitting ? t("submitting") : t("signUpBusinessBtn")}
            </button>
          </motion.div>
        </form>
      </motion.div>
    </div>
  );
}
